/* eslint-disable no-underscore-dangle */
/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useParams } from 'react-router-dom';
import {
  CheckCircleIcon as CheckCircleIconOutline,
  ClockIcon,
  DocumentTextIcon,
} from '@heroicons/react/outline';
import { PlusIcon, PencilIcon, CheckCircleIcon } from '@heroicons/react/solid';
import { useTranslation } from 'react-i18next';
import CreateLessonModal from './createLessonModal';
import { startLesson } from '../../../redux/services/progressService';
import useProgress from '../../../hooks/useProgress';

const Status = ({ status }) => {
  switch (status) {
    case 'completed':
      return (
        <div className="w-full flex justify-end space-x-3 items-center text-sky">
          <p className="w-24">Afgerond</p>
          <CheckCircleIconOutline className="h-5 w-5 " />
        </div>
      );
    case 'started':
      return (
        <div className="w-full flex justify-end space-x-3 items-center text-grey-dark">
          <p className="w-24">Gestart</p> <ClockIcon className="h-5 w-5 " />
        </div>
      );
    default:
      return (
        <div className="w-full flex justify-end space-x-3 items-center text-grey-medium_light">
          <p className="w-24"> Niet gestart</p>
          <CheckCircleIconOutline className="h-5 w-5 " />
        </div>
      );
  }
};

function ListItem(props) {
  const {
    title,
    duration,
    status,
    index,
    items,
    lessonId,
    courseId,
    isAdmin,
    onStart,
  } = props;
  const { t } = useTranslation();
  return (
    <li
      className={`w-full flex gap-8 border-t-2 ${
        index !== 0 ? 'border-grey-light' : 'border-none'
      }`}
    >
      <div className="w-full py-3 px-4 text-left grid grid-cols-3">
        <div className="flex items-center text-grey-dark">
          {`${index + 1}.\t`}
          {title}
        </div>
        <div className="flex space-x-3 justify-end col-span-2 items-center">
          <div className="hidden lg:flex space-x-3">
            <span className="flex gap-x-1">
              <DocumentTextIcon className="h-5 w-5 text-grey-medium" />
              <p className="text-grey-medium">{items}</p>
            </span>
            <span className="flex gap-x-1 border-r-2 border-grey-medium_light pr-4">
              <ClockIcon className="h-5 w-5 text-grey-medium" />
              <p className="text-grey-medium">{duration}</p>
            </span>
          </div>

          <span className="flex gap-x-3 items-center">
            <Status status={status} />
          </span>
          <Link
            className="flex justify-end"
            to={`/courses/${courseId}/lessons/${lessonId}/item/0`}
            onClick={() => startLesson(lessonId)}
          >
            <button className="bg-sky rounded-md text-white py-1 px-4 hover:bg-sky-dark">
              Start
            </button>
          </Link>
        </div>
      </div>

      {/* Admin edit button */}
      {isAdmin && (
        <div className="hidden lg:block py-3 px-4 mt-1">
          <Link
            to={`lessons/${lessonId}/update`}
            className="bg-none flex gap-2 text-sky hover:text-sky-dark my-auto"
          >
            <PencilIcon className="h-5 " />
            <p className="">Edit</p>
          </Link>
        </div>
      )}
    </li>
  );
}

ListItem.propTypes = {
  title: PropTypes.string.isRequired,
  duration: PropTypes.number.isRequired,
  completed: PropTypes.bool.isRequired,
  index: PropTypes.number.isRequired,
  items: PropTypes.number.isRequired,
  lessonId: PropTypes.string.isRequired,
  courseId: PropTypes.string.isRequired,
  isAdmin: PropTypes.bool.isRequired,
};

function LessonOverview(props) {
  const { lessons, isAdmin } = props;
  const { t } = useTranslation();
  const [lessonModalOpen, setLessonModalOpen] = useState(false);
  const progressQuery = useProgress();
  const params = useParams();

  if (!progressQuery.isSuccess) return null;

  const checkCompleted = (lessonId) => {
    try {
      const courseProgress = progressQuery.data.find(
        (e) => e.course._id === params.courseId
      );

      if (!courseProgress) return false;
      const lessonProgress = courseProgress.modules.find((module) => {
        return module.module === lessonId;
      });

      return lessonProgress?.status;
    } catch (error) {
      return false;
    }
  };

  return (
    <section className="flex-col space-y-6">
      <h1 className="text-grey-dark font-bold text-xl">
        {t('courses.modules')}
      </h1>

      {/* List of lessons in course */}
      <ul className="flex-col shadow-md bg-white rounded-md">
        {lessons.map((lesson, index) => (
          <ListItem
            key={lesson._id}
            status={checkCompleted(lesson._id)}
            duration={lesson.duration || 1}
            items={lesson.items.length}
            index={index}
            lessonId={lesson._id}
            courseId={lesson.course}
            isAdmin={isAdmin}
            title={lesson.title}
            onStart={startLesson}
          />
        ))}
      </ul>

      {/* New lesson modal */}
      <CreateLessonModal
        open={lessonModalOpen}
        setOpen={setLessonModalOpen}
        submit={() => {}}
      />

      {/* Create Lesson Button */}
      {isAdmin && (
        <button
          onClick={() => {
            setLessonModalOpen(true);
          }}
          className="p-2 flex gap-2 justify-between text-white bg-sky rounded-md shadow-md hover:shadow-lg hover:bg-sky-dark"
        >
          <PlusIcon className="h-5 m-auto" />
          <p>{t('courses.create_module')}</p>
        </button>
      )}
    </section>
  );
}

LessonOverview.propTypes = {
  lessons: PropTypes.arrayOf(PropTypes.object),
  isAdmin: PropTypes.bool.isRequired,
};

export default LessonOverview;
